import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  FormElement,
  Loader,
  ConditionsCheckbox,
  AccountDetailsReview,
  InstallationDetailsReview,
  Button,
  VoucherErrorPopUp,
} from '@sky-tv-group/components';
import { useLocation } from 'react-router-dom';
import { useReview } from '../../hooks/useReview';
import { useHistory } from 'react-router-dom'; // React Router v5 for React 17
import {
  SPLITIO_KEY,
  useSplitIO,
  formatMoney,
  useTotals,
  links,
  useProductInCart,
  CheckoutStates,
  useboxesStore,
  productSkuIds,
  voucherCodeError,
  useUpgradableBoxesStore,
  useToastContainer,
  parseQuery,
} from '@sky-tv-group/shared';
import { useCheckoutManager } from '../../hooks/useCheckoutManager';

import { myAccountSWR } from '../../services';
import { back, edit } from './navigation';
import { PRIVACY_POLICY, TERMS_AND_CONDITIONS_DOMESTIC, IS_CAPTCHA_ENABLED, CAPTCHA_KEY } from '../../config';
import { BackButton } from '../../components/back-button';
import { BreadcrumbContainer } from '../../components/BreadcrumbContainer';

const Confirm = () => {
  const location = useLocation();
  useSplitIO(SPLITIO_KEY.SKYWEB_ALLOW_MARKETING_MESSAGE);
  const { hasPackage, hasBroadband } = useCheckoutManager();
  const {
    form,
    prequal,
    submitting,
    captchaRef,
    accountDetails,
    broadbandDetails,
    broadbandDeliveryDetails,
    keyThingsToKnowField,
    termsAndConditionsField,
    errorCode,
    setSubmitting,
    sendEReciept,
  } = useReview();

  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    let { callback } = parseQuery(location.search);
    // Only process if there's a callback parameter
    if (!callback) return;
    if (callback === 'success') {
      // Reset counter on success
      sessionStorage.setItem('paymentFailureCount', '0');
      sessionStorage.removeItem('paymentToastShown');

      setPaymentLocked(false); // <— unlock
      sessionStorage.removeItem('lastProcessedFailureUrl');
      setTimeout(() => {
        sendEReciept();
        history.push('/confirmation');
      }, 2000);
    } else if (callback === 'failure') {
      // Get current failure count from sessionStorage and parse as number
      const currentCount = parseInt(sessionStorage.getItem('paymentFailureCount') || '0', 10);
      const newCount = currentCount + 1;
      sessionStorage.setItem('paymentFailureCount', newCount.toString());
      if (newCount >= 3) {
        // Check if toast was already shown

        setPaymentLocked(true);
        const toastShown = sessionStorage.getItem('paymentToastShown');
        if (!toastShown) {
          sessionStorage.setItem('paymentToastShown', 'true');
          setSubmitting(false);
          removeAllToasts();
          addToast({
            title: 'Alert',
            type: 'error',
            message: 'You have reached the maximum payment attempts allowed. Your order cannot proceed at this time.',
            closeText: 'Return Home',
            onClose: async () => {
              sessionStorage.setItem('paymentFailureCount', '0');
              sessionStorage.removeItem('paymentToastShown');
              sessionStorage.removeItem('lastProcessedFailureUrl');
              setPaymentLocked(false); // <— unlock when user closes toast and we reset
              let base = (process.env.REACT_APP_DOMAIN || '').trim();
              if (/^\/\//.test(base)) {
                base = `https:${base}`;
              }
              if (!/^https?:\/\//i.test(base)) {
                base = `${window.location.protocol}//${base}`;
              }
              const url = new URL(base);
              url.searchParams.set('v', 'old');
              window.location.replace(url.toString());
            },
          });
        }
      }
    }
  }, []);

  const totals = useTotals(myAccountSWR);
  const history = useHistory();
  const { addToast, removeAllToasts } = useToastContainer();

  const { broadbandProductInCart } = useProductInCart();
  const { requireDeliveryOnly } = useProductInCart();
  const installationDetails = useUpgradableBoxesStore(s => s.details);
  const boxes = useboxesStore(s => s.boxes);

  const hasNonPod = boxes.some(
    b => b.boxType === 'NEW' && !b.products.some(p => p.sku === productSkuIds.skyPod.primary)
  );

  const isNewFiber = prequal?.isNewFibreJourney();
  const showBroadbandInstallationDetails =
    hasBroadband &&
    (broadbandDeliveryDetails?.installationAddress ||
      (isNewFiber ? broadbandDetails?.installationDate : broadbandDetails?.activationDate) ||
      (isNewFiber ? broadbandDetails?.installationTime : undefined));

  const onTermsAndConditionsChanged = (value: boolean) => termsAndConditionsField.setValue(value);
  const onKeyThingsToKnowFieldChanged = (value: boolean) => keyThingsToKnowField.setValue(value);

  const sectionTitle = requireDeliveryOnly ? 'Delivery Details' : 'Installation Details';

  const [paymentLocked, setPaymentLocked] = useState(() => {
    try {
      const count = parseInt(sessionStorage.getItem('paymentFailureCount') || '0', 10);
      return count >= 3;
    } catch {
      return false;
    }
  });

  let stickyRef = useRef<HTMLDivElement>(null) as any;

  function handleStickyBarScroll() {
    let footerElement = document.querySelector('#footerTermsAndCond') as any;
    if (stickyRef.current) {
      if (window.scrollY >= document.body.scrollHeight - window.innerHeight) {
        footerElement.style.marginBottom = stickyRef.current.offsetHeight + 'px';
      } else {
        footerElement.style.marginBottom = '0px';
      }
    }
  }
  useEffect(() => {
    window.addEventListener('scroll', handleStickyBarScroll);
    return () => {
      window.removeEventListener('scroll', handleStickyBarScroll);
    };
  }, []);

  const onEdit = (state: string) => {
    edit(state);
  };

  const handleBack = () => {
    back(CheckoutStates.REVIEW);
  };

  if (errorCode && errorCode === voucherCodeError) return <VoucherErrorPopUp />;

  return (
    <div>
      <BreadcrumbContainer progressBarStatus={false} />
      <div
        className="container m-auto md:pb-10 lg:mt-16 lg:px-6 px-4 mt-4 text-primary-mid-night"
        data-testid="confirm-form">
        <div className="flex flex-col justify-center items-center py-4 md:w-100 lg:hidden">
          <BackButton
            buttontext={broadbandProductInCart ? 'Go back to Broadband Installation' : 'Go back to Sky TV Installation'}
            onClick={() => back(CheckoutStates.REVIEW)}
          />
        </div>
        <FormElement {...form.props}>
          <h4 className="md:sky-h5-black  sky-h6-black text-primary-mid-night-blue md:pb-10 md:pt-0 py-4">
            Account Details
          </h4>
          <AccountDetailsReview accountDetails={accountDetails} edit={edit} showSectionHeader={false} />

          <h4 className="md:sky-h5-black sky-h6-black text-primary-mid-night-blue md:py-10 pt-7 pb-4">
            {sectionTitle}
          </h4>
          <InstallationDetailsReview
            hasPackage={hasPackage && hasNonPod}
            hasBroadband={hasBroadband}
            edit={onEdit}
            accountDetails={accountDetails}
            installationDetails={installationDetails}
            broadbandDetails={broadbandDetails}
            broadbandDeliveryDetails={broadbandDeliveryDetails}
            isNewFiber={prequal?.isNewFibreJourney()}
            showSectionHeader={false}
            showMainHeader={false}
            showBroadbandInstallationDetails={!!showBroadbandInstallationDetails}
          />

          <div className="flex flex-col p-4 mb-4">
            <h4 className="flex-1 sky-h6-bold md:sky-h6-bold mb-4">Terms & Conditions</h4>
            <ConditionsCheckbox id="key-things" field={keyThingsToKnowField} onChange={onKeyThingsToKnowFieldChanged}>
              <label htmlFor="key-things">
                I have read the &nbsp;
                <a
                  className="underline cursor-pointer text-primary-day-light"
                  href={links.tvKeyThings}
                  target="_blank"
                  rel="noreferrer noopener">
                  Key Things to Know
                </a>
              </label>
            </ConditionsCheckbox>
            <ConditionsCheckbox
              id="broadband-terms"
              field={termsAndConditionsField}
              onChange={onTermsAndConditionsChanged}>
              <label htmlFor="broadband-terms">
                I accept the &nbsp;
                <a
                  className="underline cursor-pointer text-primary-day-light"
                  href={TERMS_AND_CONDITIONS_DOMESTIC}
                  target="_blank"
                  rel="noreferrer noopener">
                  Sky Consumer Terms
                </a>
              </label>
              ,&nbsp;
              {broadbandProductInCart && (
                <a
                  className="underline cursor-pointer text-primary-day-light"
                  href={links.bbTerms}
                  target="_blank"
                  rel="noreferrer noopener">
                  Sky Broadband Terms,
                </a>
              )}
              &nbsp; and &nbsp;
              <a
                className="underline cursor-pointer text-primary-day-light"
                href={PRIVACY_POLICY}
                target="_blank"
                rel="noreferrer noopener">
                Privacy Policy
              </a>
            </ConditionsCheckbox>

            <p className="pl-12 text-primary-mid-night">
              To place your order, we'll perform a credit check which involves sharing your personal information with
              Centrix. See our{' '}
              <a
                className="underline cursor-pointer text-primary-day-light"
                href={PRIVACY_POLICY}
                target="_blank"
                rel="noreferrer noopener">
                Privacy Policy
              </a>{' '}
              for details. If you do not wish to proceed with the credit check, please do not place order.
            </p>
          </div>
          {IS_CAPTCHA_ENABLED && (
            <ReCAPTCHA
              ref={captchaRef as any}
              size="invisible"
              badge="bottomright"
              sitekey={CAPTCHA_KEY}
              theme="light"
            />
          )}
          {submitting && (
            <div className="w-full h-full z-50 fixed bottom-0 top-0 right-0 left-0">
              <Loader bgOpacity={true} bgColor={'bg-neutral-matte-black'} />
            </div>
          )}
          <div className=" rounded-l border overflow-hidden text-primary-white  mt-12">
            <div className="flex justify-between bg-primary-bright-blue p-4 md:sky-h5-bold ">
              <div className=" text-primary-white">Your First Bill Estimate</div>
              <div>
                <strong>{formatMoney(totals.priceYouPayToday + totals.monthlyPriceWithOffer)}</strong>
              </div>
            </div>
            <div
              className="block fixed
             min-w-full bottom-0 lg:static z-50 md:static left-0"
              ref={stickyRef}>
              <div
                className={`flex justify-between md:p-12 lg:p-12 lg:static md:static min-w-full bottom-0 z-50 lg:shadow-none md:shadow-none lg:rounded-xs md:rounded-xs sticky-custom bg-primary-white left-0 p-5`}>
                <Button
                  data-testid="back-to-dth-form-button"
                  colorMode="light"
                  variant="secondary"
                  onClick={handleBack}
                  disabled={false}
                  className="hidden lg:block md:block">
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || paymentLocked}
                  className="min-w-full min-w-fit sky-bg-lime"
                  colorMode="success">
                  {submitting ? 'Submitting' : 'Place Order'}
                </Button>
              </div>
            </div>
          </div>
        </FormElement>
      </div>
    </div>
  );
};

export { Confirm };
